/// <reference types="vite-plugin-pwa/client" />
import { useEffect, useSyncExternalStore } from "react";
import { toast } from "sonner";
import { registerSW } from "virtual:pwa-register";

/**
 * The installed-app half of the web client: the service worker's update prompt and the
 * install entry. Imported by main.tsx before anything renders, because
 * `beforeinstallprompt` can fire before React mounts and is not fired again.
 *
 * The worker is registered in `prompt` mode: a new deploy is downloaded in the
 * background and waits, and the user reloads into it from a toast, so nothing they are
 * typing is reloaded out from under them. Without a check a tab left open for days would
 * never look, so it asks for an update every hour and whenever the app comes back to the
 * foreground — which on a phone is how an installed app is actually used.
 */

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

type PwaState = { needRefresh: boolean; installPrompt: BeforeInstallPromptEvent | null; installed: boolean };

let state: PwaState = { needRefresh: false, installPrompt: null, installed: false };
const listeners = new Set<() => void>();
const set = (patch: Partial<PwaState>) => {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const UPDATE_CHECK_MS = 60 * 60 * 1000;

const updateServiceWorker =
  "serviceWorker" in navigator
    ? registerSW({
        onNeedRefresh: () => set({ needRefresh: true }),
        onRegisteredSW: (_url, registration) => {
          if (!registration) return;
          const check = () => {
            if (navigator.onLine) void registration.update().catch(() => undefined);
          };
          setInterval(check, UPDATE_CHECK_MS);
          document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") check();
          });
        },
      })
    : null;

window.addEventListener("beforeinstallprompt", (event) => {
  // Keep the browser's own mini-infobar out of the way; the More tray offers it instead.
  event.preventDefault();
  set({ installPrompt: event as BeforeInstallPromptEvent });
});
window.addEventListener("appinstalled", () => set({ installPrompt: null, installed: true }));

export const isStandalone = (): boolean =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

/** iOS and iPadOS, where only Safari's Share → Add to Home Screen installs. */
export const isIos = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.userAgent.includes("Macintosh") && navigator.maxTouchPoints > 1);

export type InstallOption = { label: string; install: () => void } | null;

/**
 * What an "Install app" row should do here, or null when there is nothing to offer:
 * already installed, or a browser that cannot install (Firefox on a desktop).
 */
export function useInstallOption(): InstallOption {
  const { installPrompt, installed } = useSyncExternalStore(subscribe, () => state);
  if (installed || isStandalone()) return null;
  if (installPrompt) {
    return {
      label: "Install app",
      install: () => {
        void installPrompt.prompt();
        void installPrompt.userChoice.then(({ outcome }) => {
          // A prompt can be shown once; dismissed, the browser offers a new one later.
          set({ installPrompt: null, installed: outcome === "accepted" });
        });
      },
    };
  }
  if (isIos()) {
    return {
      label: "Add to Home Screen",
      install: () =>
        toast("Install on this device", {
          description: "In Safari, tap Share, then “Add to Home Screen”.",
          duration: 10000,
        }),
    };
  }
  return null;
}

/** Shows the "update available" toast once a new deploy is waiting. Call once, in App. */
export function usePwaUpdateToast(): void {
  const { needRefresh } = useSyncExternalStore(subscribe, () => state);
  useEffect(() => {
    if (!needRefresh || !updateServiceWorker) return;
    toast("A new version of the board is ready", {
      id: "pwa-update",
      duration: Infinity,
      action: { label: "Reload", onClick: () => void updateServiceWorker(true) },
    });
  }, [needRefresh]);
}
